import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { type StaffPermission } from '@icebreaker/shared/staff';
import { buttonClassName, EmptyState, PageShell } from '@icebreaker/ui';
import { ROUTES } from '@/app/routes';
import { screenPermission } from '@/app/screen-permissions';
import {
  selectHasStaffPermission,
  selectVisibleScreenRoutes,
} from '@/features/auth/store/auth.selectors';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppSelector } from '@/shared/store/hooks';

/**
 * YETKİ KAPISI — EKRAN ÖLÇEĞİ (QA S2/S3 · P2-1).
 *
 * `StaffGate` "oturumun var mı" sorusunu yanıtlar; bu kapı "bu ekranı
 * kullanabilir misin" sorusunu. İkisi ayrıdır: `staff_accounts`ta satırı olan
 * HERKES panele girer, hangi ekranın veri döndüreceğini rolün yetkileri
 * belirler.
 *
 * GÜVENLİK SINIRI DEĞİLDİR (gerçek sınır `StaffPermissionsGuard`). Buradaki
 * karar yalnız "ne çizilecek"tir — ve asıl mesele NE ÇİZİLMEYECEĞİDİR: eski
 * hâlde ekran normal açılıyor, veri isteği 403 alıyor ve kullanıcı
 * "Kuyruk yüklenemedi · Tekrar dene" görüyordu. "Tekrar dene" hiçbir zaman
 * başarılı olamaz; kullanıcıyı sonsuz bir döngüde bırakır.
 *
 * DÜRÜST YÜZEY: ne olduğunu söyler, tekrar deneme SUNMAZ ve personeli
 * kullanabileceği bir ekrana götürür.
 */
export function ScreenPermissionGate({ route, children }: { route: string; children: ReactNode }) {
  return (
    <PermissionSurfaceGate permission={screenPermission(route)} fallback={<NoPermissionScreen />}>
      {children}
    </PermissionSurfaceGate>
  );
}

/**
 * YETKİ KAPISI — ALT YÜZEY ÖLÇEĞİ.
 *
 * Ekranın tamamı erişilebilirken içindeki bir karar panosunun ayrı yetki
 * istediği durum (moderasyon kuyruğundaki sahiplenme kararı). Yüzey ÇİZİLMEZ;
 * yerine tek satırlık dürüst bir not kalır — kalem listede görünmeye devam
 * eder, yalnız kararı verilemez.
 *
 * `permission` TANIMSIZSA KAPI KAPALIDIR (fail-closed): envantere yazılmamış
 * bir yüzey "yetki istemiyor" sayılamaz.
 */
export function PermissionSurfaceGate({
  permission,
  fallback,
  children,
}: {
  permission: StaffPermission | undefined;
  fallback?: ReactNode;
  children: ReactNode;
}) {
  const t = useTranslation();
  const allowed = useAppSelector(
    permission === undefined ? () => false : selectHasStaffPermission(permission),
  );

  if (allowed) return <>{children}</>;
  return (
    <>
      {fallback ?? <p data-permission-note="true">{t.t('admin.panel.permission.surfaceNote')}</p>}
    </>
  );
}

/**
 * Boş durum dili (README 4.7): ne olduğu + tek DOLU eylem. İllüstrasyon yok,
 * "tekrar dene" yok — eylem personeli GERÇEKTEN kullanabileceği bir ekrana
 * götürür. Kullanabileceği ekran YOKSA (yetkisi tamamen boşalmış hesap) tek
 * eylem çıkıştır; ölü bir bağlantı gösterilmez.
 */
function NoPermissionScreen() {
  const t = useTranslation();
  const visible = useAppSelector(selectVisibleScreenRoutes);
  const target = visible[0];

  /* Kabuğun `<main>`i içinde çizilir: kendi `main`i olursa işaret iç içe girer. */
  return (
    <PageShell rail="reading" as="section">
      <EmptyState
        title={t.t('admin.panel.permission.title')}
        body={t.t('admin.panel.permission.body')}
        action={
          target !== undefined ? (
            <Link className={buttonClassName('primary')} to={target}>
              {t.t('admin.panel.permission.action')}
            </Link>
          ) : (
            <Link className={buttonClassName('primary')} to={ROUTES.signIn}>
              {t.t('admin.panel.permission.signOut')}
            </Link>
          )
        }
      />
    </PageShell>
  );
}
